"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowLeft,
  Calendar,
  Share2,
  Tag,
  ListTree,
  BookOpen,
  ChevronRight,
  MapPin,
} from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams: () => Record<string, any>;
    };
  }
}

interface TocItem {
  id: string;
  text: string;
  level: number;
}

function extractHeadings(contentHtml: string): TocItem[] {
  if (!contentHtml) return [];
  const regex = /<h([23])[^>]*>(.*?)<\/h\1>/gi;
  const items: TocItem[] = [];
  let match: RegExpExecArray | null;
  let idx = 0;
  while ((match = regex.exec(contentHtml)) !== null) {
    const level = parseInt(match[1], 10);
    const text = match[2].replace(/<[^>]*>/g, "").trim();
    if (text) {
      items.push({
        id: `heading-section-${idx++}`,
        text,
        level,
      });
    }
  }
  return items;
}

function TableOfContentsWidget({
  headings,
  lang,
}: {
  headings: TocItem[];
  lang: "vi" | "en";
}) {
  if (!headings || headings.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center gap-2 border-b border-border pb-2.5">
        <ListTree className="size-4 text-secondary" />
        <h3 className="font-serif text-sm md:text-base font-bold text-foreground">
          {lang === "vi" ? "Mục lục" : "Table of Contents"}
        </h3>
      </div>
      <nav aria-label="Table of contents" className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {headings.map((item, i) => (
          <Link
            key={item.id || i}
            to={`#${item.id}`}
            className={`block rounded-md py-1.5 text-xs transition-colors hover:text-primary ${
              item.level === 3
                ? "pl-4 text-muted-foreground"
                : "font-semibold text-foreground"
            }`}
          >
            {item.text}
          </Link>
        ))}
      </nav>
    </div>
  );
}

function RelatedPostsConsumer({
  currentPost,
  lang,
}: {
  currentPost: any;
  lang: "vi" | "en";
}) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const currentCatIds = (currentPost?.categories || []).map((c: any) => c.id || c.name);
  const otherPosts = (posts || []).filter(
    (p) => String(p.id) !== String(currentPost?.id) && p.slug !== currentPost?.slug
  );

  const matched = otherPosts.filter((p) => {
    if (currentCatIds.length === 0) return true;
    const cats = p.categories || [];
    return cats.some((c: any) => currentCatIds.includes(c.id || c.name));
  });

  const displayList = matched.length > 0 ? matched.slice(0, 3) : otherPosts.slice(0, 3);

  if (displayList.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 border-b border-border pb-2.5">
        <h3 className="font-serif text-sm md:text-base font-bold text-foreground">
          {lang === "vi" ? "Bài viết liên quan" : "Related Articles"}
        </h3>
      </div>
      <div className="space-y-3">
        {displayList.map((p, i) => (
          <article
            key={p.id || i}
            data-index={i}
            className="border-b border-border/70 pb-3 last:border-b-0 last:pb-0"
          >
            <Link
              to={p.link || "/ParentGuideResourcesPage"}
              className="text-xs md:text-sm font-semibold text-foreground hover:text-primary transition-colors line-clamp-2"
            >
              {p.title}
            </Link>
            {p.date && (
              <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                <Calendar className="size-3" />
                {new Date(p.date).toLocaleDateString(lang === "vi" ? "vi-VN" : "en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

function RelatedProgramsConsumer({
  currentPost,
  lang,
}: {
  currentPost: any;
  lang: "vi" | "en";
}) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2].map((i) => (
          <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const queryKeywords = `${currentPost?.title || ""} ${(currentPost?.categories || [])
    .map((c: any) => c.name)
    .join(" ")}`
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 3);

  const matched = (posts || [])
    .filter((prog) => {
      const progName = (prog.customFields?.program_name || prog.title || "").toLowerCase();
      const skills = (prog.customFields?.skills || "").toLowerCase();
      return queryKeywords.some((k) => progName.includes(k) || skills.includes(k));
    })
    .slice(0, 2);

  const displayPrograms = matched.length > 0 ? matched : (posts || []).slice(0, 2);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 border-b border-border pb-2.5">
        <h3 className="font-serif text-sm md:text-base font-bold text-foreground">
          {lang === "vi" ? "Chương trình liên quan" : "Related Programs"}
        </h3>
      </div>
      <div className="space-y-2.5">
        {displayPrograms.length > 0 ? (
          displayPrograms.map((prog, i) => (
            <div
              key={prog.id || i}
              data-index={i}
              className="rounded-lg border border-border p-3 hover:border-secondary transition-colors"
            >
              <h4 className="text-xs md:text-sm font-semibold text-foreground line-clamp-1">
                {prog.customFields?.program_name || prog.title}
              </h4>
              {prog.customFields?.age_range && (
                <span className="mt-1 inline-block rounded-md bg-secondary/15 px-2 py-0.5 font-mono text-[10px] font-semibold text-secondary-foreground">
                  {prog.customFields.age_range}
                </span>
              )}
              <div className="mt-2">
                <Link
                  to={prog.link || "/ProgramsPage"}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  {lang === "vi" ? "Xem chương trình" : "View program"}
                  <ChevronRight className="size-3" />
                </Link>
              </div>
            </div>
          ))
        ) : (
          <Link
            to="/ProgramsPage"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            {lang === "vi" ? "Khám phá các khóa học" : "Explore courses"}
            <ChevronRight className="size-3" />
          </Link>
        )}
      </div>
    </div>
  );
}

function RelatedProjectsConsumer({
  currentPost,
  lang,
}: {
  currentPost: any;
  lang: "vi" | "en";
}) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2].map((i) => (
          <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const queryKeywords = `${currentPost?.title || ""} ${(currentPost?.categories || [])
    .map((c: any) => c.name)
    .join(" ")}`
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 3);

  const matched = (posts || [])
    .filter((proj) => {
      const projName = (proj.customFields?.project_name || proj.title || "").toLowerCase();
      const domain = (proj.customFields?.field_domain || "").toLowerCase();
      const skills = (proj.customFields?.skills || "").toLowerCase();
      return queryKeywords.some(
        (k) => projName.includes(k) || domain.includes(k) || skills.includes(k)
      );
    })
    .slice(0, 2);

  const displayProjects = matched.length > 0 ? matched : (posts || []).slice(0, 2);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 border-b border-border pb-2.5">
        <h3 className="font-serif text-sm md:text-base font-bold text-foreground">
          {lang === "vi" ? "Dự án liên quan" : "Related Projects"}
        </h3>
      </div>
      <div className="space-y-2.5">
        {displayProjects.length > 0 ? (
          displayProjects.map((proj, i) => (
            <div
              key={proj.id || i}
              data-index={i}
              className="rounded-lg border border-border p-3 hover:border-secondary transition-colors"
            >
              <h4 className="text-xs md:text-sm font-semibold text-foreground line-clamp-1">
                {proj.customFields?.project_name || proj.title}
              </h4>
              {proj.customFields?.field_domain && (
                <p className="mt-1 text-[11px] text-muted-foreground line-clamp-1">
                  {proj.customFields.field_domain}
                </p>
              )}
              <div className="mt-2">
                <Link
                  to={proj.link || "/ProjectsCompetitionsPage#student-projects-gallery"}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  {lang === "vi" ? "Xem chi tiết dự án" : "View project build"}
                  <ChevronRight className="size-3" />
                </Link>
              </div>
            </div>
          ))
        ) : (
          <Link
            to="#"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            {lang === "vi" ? "Xem dự án học sinh" : "View student projects"}
            <ChevronRight className="size-3" />
          </Link>
        )}
      </div>
    </div>
  );
}

function NearbyCentersConsumer({ lang }: { lang: "vi" | "en" }) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2].map((i) => (
          <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const centers = (posts || []).slice(0, 2);
  if (centers.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 border-b border-border pb-2.5">
        <h3 className="font-serif text-sm md:text-base font-bold text-foreground">
          {lang === "vi" ? "Cơ sở gần bạn" : "Nearby Centers"}
        </h3>
      </div>
      <div className="space-y-3">
        {centers.map((c, i) => (
          <div
            key={c.id || i}
            data-index={i}
            className="border-b border-border/70 pb-2.5 last:border-b-0 last:pb-0"
          >
            <p className="text-xs md:text-sm font-semibold text-foreground">
              {c.customFields?.center_name || c.title}
            </p>
            {c.customFields?.address && (
              <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                {c.customFields.address}
              </p>
            )}
            <Link
              to="/FacilitiesCentersPage"
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <MapPin className="size-3 text-secondary" />
              {lang === "vi" ? "Chỉ đường & liên hệ" : "Directions & contact"}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function SinglePostContent() {
  const { posts, loading, error, refetch } = usePosts();
  const [lang, setLang] = useState<"vi" | "en">("vi");

  useEffect(() => {
    const getStoredLang = () => {
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") return stored;
      }
      return "vi";
    };
    setLang(getStoredLang());

    const handleLangChange = (e: any) => {
      if (e?.detail?.lang) {
        setLang(e.detail.lang);
      } else {
        setLang(getStoredLang());
      }
    };

    window.addEventListener("roboto-lang-change" as any, handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change" as any, handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);

  const relatedPostsQuery = useMemo(
    () => new WP_Query({ post_type: "post", posts_per_page: 8 }),
    []
  );
  const relatedProgramsQuery = useMemo(
    () => new WP_Query({ post_type: "program", posts_per_page: 6 }),
    []
  );
  const relatedProjectsQuery = useMemo(
    () => new WP_Query({ post_type: "project", posts_per_page: 6 }),
    []
  );
  const nearbyCentersQuery = useMemo(
    () => new WP_Query({ post_type: "center", posts_per_page: 4 }),
    []
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="h-6 w-48 rounded-md bg-muted animate-pulse" />
        <div className="h-12 w-3/4 rounded-md bg-muted animate-pulse" />
        <div className="flex gap-4">
          <div className="h-6 w-24 rounded-md bg-muted animate-pulse" />
          <div className="h-6 w-28 rounded-md bg-muted animate-pulse" />
        </div>
        <div className="aspect-video w-full rounded-2xl bg-muted animate-pulse" />
        <div className="space-y-3 pt-4">
          <div className="h-4 w-full rounded-md bg-muted animate-pulse" />
          <div className="h-4 w-5/6 rounded-md bg-muted animate-pulse" />
          <div className="h-4 w-4/6 rounded-md bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <p className="text-destructive font-medium mb-4">
          {lang === "vi"
            ? "Không thể tải bài viết vào lúc này."
            : "Unable to display post at this moment."}
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          {lang === "vi" ? "Thử lại" : "Retry Loading"}
        </Button>
      </div>
    );
  }

  const post = posts && posts.length > 0 ? posts[0] : null;

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">
          {lang === "vi" ? "Không tìm thấy bài viết" : "Post not found"}
        </h2>
        <p className="text-muted-foreground mb-6">
          {lang === "vi"
            ? "Bài viết được yêu cầu không thể lấy từ cơ sở dữ liệu."
            : "The requested article could not be retrieved from the database."}
        </p>
        <Button asChild>
          <Link to="/ProgramsPage">
            {lang === "vi" ? "Khám phá các chương trình" : "Explore our programs"}
          </Link>
        </Button>
      </div>
    );
  }

  const authorName = post.author?.name || "ROBOTO Faculty";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";
  const postCategories = post.categories || [];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(lang === "vi" ? "vi-VN" : "en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : lang === "vi"
    ? "Góc nhìn học thuật"
    : "Curriculum Insight";

  const headings = extractHeadings(post.content || "");

  const contentWithHeadingIds = (() => {
    if (!post.content) return "";
    let idx = 0;
    return post.content.replace(
      /<h([23])([^>]*)>(.*?)<\/h\1>/gi,
      (_match, tag, attrs, text) => {
        const id = `heading-section-${idx++}`;
        return `<h${tag} id="${id}" ${attrs}>${text}</h${tag}>`;
      }
    );
  })();

  return (
    <article className="mx-auto max-w-7xl">
      {/* Spec rail line */}
      <div className="mb-6 flex items-center gap-2">
        <span className="size-2 rounded-sm bg-secondary" />
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* (1) Breadcrumb row */}
      <nav
        aria-label="Breadcrumb"
        className="mb-6 flex flex-wrap items-center gap-2 text-xs md:text-sm text-muted-foreground"
      >
        <Link to="/" className="hover:text-foreground transition-colors">
          {lang === "vi" ? "Trang chủ" : "Home"}
        </Link>
        <span className="text-border">/</span>
        <Link to="/ParentGuideResourcesPage" className="hover:text-foreground transition-colors">
          {lang === "vi" ? "Tài nguyên" : "Resources"}
        </Link>
        <span className="text-border">/</span>
        <span className="text-foreground font-medium line-clamp-1 max-w-[220px] sm:max-w-xs md:max-w-md">
          {post.title}
        </span>
      </nav>

      {/* Back button */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="text-muted-foreground hover:text-foreground pl-0"
        >
          <Link
            to="/ParentGuideResourcesPage"
            className="inline-flex items-center gap-2 text-sm font-medium"
          >
            <ArrowLeft className="size-4" />
            {lang === "vi" ? "Quay lại tài nguyên" : "Back to resources"}
          </Link>
        </Button>
      </div>

      {/* Header Info */}
      <header className="mb-8">
        {/* (2) Category Badges without reading-time estimate */}
        <div className="mb-4 flex flex-wrap items-center gap-2.5">
          {postCategories.length > 0 ? (
            postCategories.map((cat, i) => (
              <span
                key={cat.id || i}
                data-index={i}
                className="rounded-lg border border-secondary/40 bg-secondary/10 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground"
              >
                {cat.name}
              </span>
            ))
          ) : (
            <span className="rounded-lg border border-secondary/40 bg-secondary/10 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              Robotics &amp; STEM
            </span>
          )}
        </div>

        {/* Post Title */}
        <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground md:text-5xl md:leading-tight">
          {post.title}
        </h1>

        {/* Author / Date / Share Strip */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-border py-4">
          <div className="flex items-center gap-3">
            <Avatar className="size-10 border border-border">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-muted text-foreground text-xs font-semibold">
                {authorName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-semibold text-foreground">{authorName}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="size-3" />
                  {formattedDate}
                </span>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (navigator?.share && post.link) {
                navigator.share({ title: post.title, url: post.link }).catch(() => {});
              }
            }}
            className="text-xs"
          >
            <Share2 className="size-3.5 mr-1" />
            {lang === "vi" ? "Chia sẻ bài viết" : "Share article"}
          </Button>
        </div>
      </header>

      {/* (3) Short intro/summary line sourced from post.excerpt only if present */}
      {post.excerpt && (
        <div
          className="mb-8 border-l-4 border-accent bg-accent/5 p-4 rounded-r-lg text-base md:text-lg font-medium text-foreground leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {/* (4) Featured Image */}
      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-2xl border border-border bg-muted">
          <img
            src={post.featuredImage}
            alt={post.title || "Featured article visual"}
            loading="lazy"
            decoding="async"
            className="aspect-video w-full object-cover"
          />
        </div>
      )}

      {/* Main Two-Column Layout: Article Body + Desktop Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Left Column: Article Body with max-w-3xl */}
        <div className="lg:col-span-8">
          {/* (5) Article body with max-w-3xl and enhanced typography */}
          <section className="text-foreground leading-relaxed">
            {contentWithHeadingIds ? (
              <div
                className="prose prose-slate max-w-3xl text-base md:text-lg leading-relaxed text-foreground [&>p]:mb-6 [&>p]:leading-relaxed [&>h2]:font-serif [&>h2]:text-2xl [&>h2]:md:text-3xl [&>h2]:font-bold [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-foreground [&>h2]:border-b [&>h2]:border-border/60 [&>h2]:pb-2 [&>h3]:font-serif [&>h3]:text-xl [&>h3]:md:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ul>li]:leading-relaxed [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>ol>li]:leading-relaxed [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:bg-secondary/10 [&>blockquote]:p-4 [&>blockquote]:rounded-r-lg [&>blockquote]:not-italic [&>blockquote]:font-normal [&>blockquote]:text-foreground [&>blockquote]:my-6 [&>pre]:bg-muted [&>pre]:text-foreground [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:font-mono [&>pre]:text-sm [&>pre]:overflow-x-auto [&>table]:w-full [&>table]:border-collapse [&>table]:my-6 [&>table_th]:border-b [&>table_th]:border-border [&>table_th]:p-3 [&>table_th]:text-left [&>table_td]:border-b [&>table_td]:border-border/60 [&>table_td]:p-3"
                dangerouslySetInnerHTML={{ __html: contentWithHeadingIds }}
              />
            ) : post.excerpt ? (
              <div
                className="prose prose-slate max-w-3xl text-base md:text-lg [&>p]:mb-6"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
              />
            ) : (
              <p className="text-muted-foreground">
                {lang === "vi"
                  ? "Nội dung chi tiết sẽ sớm được cập nhật."
                  : "Full curriculum briefing will be posted soon."}
              </p>
            )}
          </section>

          {/* Post Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-border pt-6 max-w-3xl">
              <Tag className="size-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground mr-1">
                {lang === "vi" ? "Thẻ:" : "Tags:"}
              </span>
              {post.tags.map((tag, i) => (
                <span
                  key={tag.id || i}
                  data-index={i}
                  className="rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-foreground"
                >
                  #{tag.name}
                </span>
              ))}
            </div>
          )}

          {/* (7) Author Card with real authorName and generic faculty role label */}
          <div className="mt-10 rounded-xl border border-border bg-card p-6 md:p-8 max-w-3xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <Avatar className="size-14 border-2 border-secondary/30">
                {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
                <AvatarFallback className="bg-primary text-primary-foreground text-base font-bold">
                  {authorName.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-secondary/15 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-secondary-foreground">
                    {lang === "vi" ? "Giảng viên ROBOTO" : "ROBOTO Faculty"}
                  </span>
                </div>
                <h3 className="mt-1.5 font-serif text-lg font-bold text-foreground">
                  {authorName}
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {lang === "vi" ? "Giảng viên ROBOTO" : "ROBOTO Faculty"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* (8) Desktop Sidebar (stacked sections on mobile appearing after article body) */}
        <aside className="lg:col-span-4 space-y-6">
          {/* (a) Table of Contents */}
          <TableOfContentsWidget headings={headings} lang={lang} />

          {/* (e) Link reading 'Cẩm nang Phụ huynh' / 'Parent Guide' */}
          <div className="rounded-xl border border-secondary/40 bg-secondary/10 p-5">
            <div className="flex items-start gap-3">
              <BookOpen className="size-5 shrink-0 text-secondary mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-foreground">
                  {lang === "vi" ? "Cẩm nang Phụ huynh" : "Parent Guide"}
                </h4>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {lang === "vi"
                    ? "Định hướng công nghệ, lộ trình học tập và kiến thức STEM dành cho phụ huynh."
                    : "Tech education pathways, curriculum milestones, and STEM roadmaps for parents."}
                </p>
                <div className="mt-3">
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="w-full text-xs border-secondary/50 hover:bg-secondary hover:text-secondary-foreground"
                  >
                    <Link to="/ParentGuideResourcesPage">
                      {lang === "vi" ? "Đọc cẩm nang" : "Read Parent Guide"}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* (b) Related Posts */}
          <PostsProvider wp_query={relatedPostsQuery}>
            <RelatedPostsConsumer currentPost={post} lang={lang} />
          </PostsProvider>

          {/* (c) Related Programs */}
          <PostsProvider wp_query={relatedProgramsQuery}>
            <RelatedProgramsConsumer currentPost={post} lang={lang} />
          </PostsProvider>

          {/* (d) Related Projects */}
          <PostsProvider wp_query={relatedProjectsQuery}>
            <RelatedProjectsConsumer currentPost={post} lang={lang} />
          </PostsProvider>

          {/* (f) Nearby Centers */}
          <PostsProvider wp_query={nearbyCentersQuery}>
            <NearbyCentersConsumer lang={lang} />
          </PostsProvider>
        </aside>
      </div>

      <Separator className="my-14" />

      {/* (9) Closing Lab CTA Card */}
      <Card className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
        <CardContent className="p-8 md:p-10">
          <div className="max-w-2xl">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary">
              {lang === "vi" ? "Buổi trải nghiệm thực hành" : "Hands-on Lab Session"}
            </span>
            <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              {lang === "vi"
                ? "Trải nghiệm phòng lab ROBOTO trực tiếp"
                : "Experience the ROBOTO lab in person"}
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              {lang === "vi"
                ? "Đăng ký buổi học thử 60 phút miễn phí dành cho lứa tuổi 4–16. Học sinh lắp ráp, lập trình và vận hành robot thật cùng sự hướng dẫn chuyên sâu từ đội ngũ giảng viên."
                : "Book a complimentary 60-minute trial workshop for ages 4–16. Students build, program, and test a real robotics chassis with personalized mentorship."}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 px-6 font-semibold">
                <Link to="/FreeTrialContactPage">
                  {lang === "vi" ? "Đăng ký học thử miễn phí" : "Book a free trial"}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ProgramsPage">
                  {lang === "vi" ? "Khám phá khóa học" : "Explore courses"}
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

export default function Post() {
  const current_wp_query_params =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : { post_type: "post", posts_per_page: 1 };

  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background py-16 md:py-24 text-foreground"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <PostsProvider className="w-full" wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>
      </div>
    </section>
  );
}